import { Box, Button, Card, IconButton, MenuItem, Stack, Typography } from '@mui/material'
import { CheckType, UnitType } from '@planning/app/api'
import { useUnitTypeIcon } from '@planning/components/UnitType'
import { formatRailcarName } from '@planning/utils/railcar-utils'
import { useTranslate } from '@tolgee/react'
import { BinIcon, HighlightedText, ThreeDotMenu, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { WarningIcon } from 'modules/ui/src/theme/overrides/CustomIcons'
import { FC } from 'react'

interface Props {
  containers: {
    number: string
    hasIssue: boolean | undefined
    unityType?: UnitType
  }[]
  railcar: string
  isMissingRailcar: boolean
  isDischargeChecked: boolean
  sequence: number
  operation?: CheckType
  searchHighlight?: string
  onSelectRailcar: () => void
  onDelete?: () => void
  onReportRailcarIssue?: () => void
  isDepartureChecked?: boolean
  isLoadPrepChecked?: boolean
}

const ContainerChip: FC<{
  containerNumber: string
  hasIssue: boolean | undefined
  unitType: UnitType
}> = ({ containerNumber, hasIssue, unitType }) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const icon = useUnitTypeIcon(unitType)
  return (
    <Box
      sx={{
        display: 'inline-block',
        p: '.25rem .75rem',
        bgcolor: theme.palette.grey[200],
        fontSize: '14px',
        borderRadius: '8px',
        mr: '6px',
        // mb: '6px',
      }}
    >
      {!hasIssue && (
        <Stack direction={'row'} spacing={1} alignItems={'center'} alignContent={'center'}>
          {icon}
          <Typography variant='subtitle2'>{containerNumber}</Typography>
        </Stack>
      )}

      {hasIssue && (
        <Stack direction={'row'} spacing={1} alignItems={'center'}>
          <span style={{ display: 'flex' }}>
            <WarningIcon color='warning' />
          </span>

          <Box>
            <Typography variant='subtitle2'>{containerNumber}</Typography>

            <Typography variant='caption'>
              {t('containerHasIssue', 'Container has issue')}
            </Typography>
          </Box>
        </Stack>
      )}
    </Box>
  )
}

export const RailCarCard: FC<Props> = observer(
  ({
    containers,
    railcar,
    isMissingRailcar,
    isDischargeChecked,
    sequence,
    searchHighlight,
    operation,
    onDelete,
    onSelectRailcar,
    onReportRailcarIssue,
    isDepartureChecked,
    isLoadPrepChecked,
  }) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()

    const isDischargeAndChecked = operation === CheckType.DischargePreparation && isDischargeChecked

    const getButtonText = () => {
      switch (operation) {
        case CheckType.DischargePreparation:
          if (isDischargeChecked) return t('edit', 'Edit')
          return t('checkIn', 'Check in')

        case CheckType.LoadPreparation:
          if (isLoadPrepChecked) return t('view', 'View')
          return t('check', 'Check')

        case CheckType.DepartureCheck:
          if (isDepartureChecked) return t('edit', 'Edit')
          return t('checkOut', 'Check out')

        default:
          return t('check', 'Check')
      }
    }

    return (
      <Card
        sx={{
          padding: '1rem',
        }}
      >
        <Stack direction={'row'} spacing={2} alignItems={'center'} mb={theme.customSpacing.s}>
          <Box
            sx={{
              display: 'inline-block',
              p: '.5rem .75rem',
              bgcolor: theme.palette.grey[900],
              color: '#fff',
              borderRadius: '8px',
            }}
          >
            {sequence}
          </Box>
          <Typography variant='h2'>
            <HighlightedText color={theme.palette.info.main} highlight={searchHighlight}>
              {formatRailcarName(railcar)}
            </HighlightedText>
          </Typography>
        </Stack>

        <Stack direction={'row'} spacing={1}>
          {isMissingRailcar && (
            <Stack
              direction={'row'}
              spacing={1}
              alignItems={'center'}
              sx={{
                p: '.25rem .75rem',
                bgcolor: theme.palette.grey[200],
                fontSize: '14px',
                borderRadius: '8px',
                mr: '6px',
                mb: '6px',
              }}
            >
              <WarningIcon color='warning' />
              <Typography variant='caption'>{t('missingRailcar', 'Missing railcar')}</Typography>
            </Stack>
          )}
          <Box alignItems={'center'}>
            {containers.map(item => (
              <ContainerChip
                key={item.number}
                containerNumber={item.number}
                hasIssue={item.hasIssue}
                unitType={item.unityType!}
              />
            ))}
          </Box>
        </Stack>

        {!isMissingRailcar && (
          <Stack direction={'row'} spacing={2} alignItems={'center'} mt={theme.customSpacing.s}>
            <Button
              variant='contained'
              size='large'
              sx={{ width: '100%', mt: '1rem' }}
              onClick={onSelectRailcar}
              data-cy={`check-or-edit-railcar-${railcar}-button`}
            >
              {getButtonText()}
            </Button>

            {onDelete && !isDischargeAndChecked && (
              <IconButton
                onClick={onDelete}
                sx={{
                  bgcolor: theme.palette.grey[200],
                }}
              >
                <BinIcon />
              </IconButton>
            )}
            {onReportRailcarIssue && (
              <ThreeDotMenu>
                <MenuItem onClick={onReportRailcarIssue}>
                  {t('reportAnIssue', 'Report an issue')}
                </MenuItem>
              </ThreeDotMenu>
            )}
          </Stack>
        )}
      </Card>
    )
  },
)
